/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import AuthPageWrapper from "./AuthPageWrapper";
import Dropdown from "../ui/SelectDropdown";
import { signupSchema } from "./auth_schemas";
import { SignupFormInputs } from "./auth_types";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { RootState } from "../../redux/app/store";
import { setSignupFormData, signup } from "../../redux/features/authSlice";
import { mapDataToLabelValue } from "../../helpers";
import { languages } from "./util";
import ModalInput from "../ui/ModalTextInput";

const languageOptions = languages.map((lang: any) => ({
  label: lang.name,
  value: lang.name,
}));

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isAuthenticated, authenticatingUser, signupFormData } =
    useAppSelector((state: RootState) => state.auth);

  const { countries } = useAppSelector((state: RootState) => state.util);

  // SAFE INITIAL FORM DATA
  const safeFormData: SignupFormInputs = {
    firstName: signupFormData?.firstName ?? "",
    lastName: signupFormData?.lastName ?? "",
    email: signupFormData?.email ?? "",
    phoneNumber: signupFormData?.phoneNumber ?? "",
    password: signupFormData?.password ?? "",
    confirmPassword: signupFormData?.confirmPassword ?? "",
    country: signupFormData?.country ?? "",
    language: signupFormData?.language ?? "",
  };
  const [formData, setFormData] = useState<SignupFormInputs>(safeFormData);
  const [errors, setErrors] = useState<any>({});

  // --------------------------
  // HANDLERS
  // --------------------------
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
    dispatch(setSignupFormData({ [name]: value }));

    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    dispatch(setSignupFormData({ [name]: value }));

    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  // --------------------------
  // SUBMIT HANDLER
  // --------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      username: formData.email,
      phoneNumber: formData.phoneNumber,
      password: formData.password,
      countryId: formData.country,
      language: formData.language,
    };

    try {
      // Validate the whole form first
      await signupSchema.validate(formData, { abortEarly: false });
      // Submit to backend
      await dispatch(signup(payload)).unwrap();
      navigate("/signup/verify-otp");
    } catch (err: any) {
      // Handle validation errors
      const validationErrors: any = {};
      err.inner?.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });

      setErrors(validationErrors);
    }
  };

  // --------------------------
  // REDIRECT IF LOGGED IN
  // --------------------------
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated]);

  return (
    <AuthPageWrapper
      title="Create an account"
      sub_title={
        <>
          Already have an account?{" "}
          <Link to="/" className="text-accent hover:underline ml-2">
            Log in
          </Link>
        </>
      }
    >
      <form className="space-y-4 w-full md:w-[450px]" onSubmit={handleSubmit}>
        <ModalInput
          label="First Name"
          name="firstName"
          type="text"
          placeholder="Enter your first name"
          value={formData.firstName}
          onChange={handleInputChange}
          error={errors.firstName}
        />

        <ModalInput
          label="Last Name"
          name="lastName"
          type="text"
          placeholder="Enter your last name"
          value={formData.lastName}
          onChange={handleInputChange}
          error={errors.lastName}
        />

        <ModalInput
          label="Email Address"
          name="email"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
        />

        <ModalInput
          label="Phone Number"
          name="phoneNumber"
          type="text"
          placeholder="Enter your phone number"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          error={errors.phoneNumber}
        />

        <Dropdown
          label="Country"
          name="country"
          options={mapDataToLabelValue(countries, "name", "id")}
          value={formData.country}
          onSelect={(value) => handleSelectChange("country", value)}
          error={errors.country}
          placeholder="Select country"
        />

        <Dropdown
          label="Language"
          name="language"
          options={languageOptions}
          value={formData.language}
          onSelect={(value) => handleSelectChange("language", value)}
          error={errors.language}
          placeholder="Select language"
        />

        <ModalInput
          label="Password"
          name="password"
          type="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
          error={errors.password}
        />

        <ModalInput
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          placeholder="Re-enter your password"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={errors.confirmPassword}
        />

        <Button label="Sign up" type="submit" isLoading={authenticatingUser} />
      </form>
    </AuthPageWrapper>
  );
};

export default SignupPage;
